import React, { useState, useContext, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getServerURL } from '../../../../../../Utils/getServerURL';
import { AddCompType } from '../../../../_Common/Const/BusinessConfigs';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FileDropTarget } from '../../../../../../BaseComponents/FileDropTarget/FileDropTarget';
import EDDataGrid from '../../../../../../BaseComponents/XGrid/EDDataGrid';
import { remove, update } from 'ramda';
import Logger from '../../../../../../Utils/Logger';
import {
  GridRenderCellParams,
  GridRowId,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import shortid from 'shortid';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import SelectFileButton from '../../../../../../BaseComponents/SelectFileButton/SelectFileButton';
import { GridApiPro } from '@mui/x-data-grid-pro/';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { useIsTouchDevice } from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';

const useStyles = makeStyles()(theme => ({
  textEdit: {
    display: 'block',
  },
  contentContainer: {
    [theme.breakpoints.up('md')]: {
      width: '600px',
    },
  },
}));

const getFileExtension = (fileName: string): string =>
  fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);

const removeFileExtension = (fileName: string): string =>
  fileName.substring(0, fileName.lastIndexOf('.'));

interface IFile {
  id: string;
  file: File;
  beskrivning: string;
  /** If file has a blobId, it has successfully been uploaded */
  blobId?: string;
}

const uploadFile = (file: File, dbName: string): Promise<string> => {
  const formData = new FormData();

  formData.append('myFile', file, file.name);
  formData.append('dbname', dbName);
  formData.append('compress', 'true');

  // Post to backend
  return fetch(`${getServerURL()}/blobupload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  }).then(response => {
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    return response.text();
  });
};

const renderBeskrivningCell = (
  params: GridRenderCellParams<any, any, any>,
  apiRef: React.RefObject<GridApiPro | null>,
  onBeskrivningChange: (rowId: GridRowId, newValue: string) => void
) => {
  return (
    <EDBox sx={{ marginLeft: 1.5, width: '100%' }}>
      <EDTextEdit
        onClick={() => {
          if (!apiRef.current) return;
          const gridEditRows = apiRef.current.state.editRows;
          if (!gridEditRows[params.row.id]) {
            apiRef.current.startRowEditMode({ id: params.row.id });
          }
        }}
        label={'Beskrivning'}
        hideLabel
        hideOutline
        fullWidth
        allMargins="none"
        value={params.value}
        onChange={newValue => {
          if (!apiRef.current) return;
          apiRef.current.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue,
          });
          onBeskrivningChange(params.id, newValue);
        }}
      />
    </EDBox>
  );
};

const FakturaDokumentAddComp: AddCompType = props => {
  const { classes } = useStyles();
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const isTouchDevice = useIsTouchDevice();

  const [selectedFiles, setSelectedFiles] = useState<IFile[]>([]);
  const [blobUploadLoading, setBlobUploadLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onDialogExited = () => {
    setSelectedFiles([]);
    setBlobUploadLoading(false);
    setErrorMessage(null);
  };

  const onBeskrivningChange = useCallback(
    (rowId: GridRowId, newValue: string) => {
      setSelectedFiles(oldValue => {
        const index = oldValue.findIndex(file => file.id === rowId);
        if (index > -1 && oldValue && oldValue[index]) {
          return update(
            index,
            { ...oldValue[index], beskrivning: newValue },
            oldValue
          );
        } else {
          Logger.error(
            new Error(
              `Tried to change file beskrivning, row with id: ${rowId} not found`
            )
          );
          return oldValue;
        }
      });
    },
    []
  );

  const setFiles = (files: FileList | null) => {
    if (files) {
      const filesWithBeskrivning: IFile[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        filesWithBeskrivning.push({
          id: shortid.generate(),
          file,
          beskrivning: removeFileExtension(file.name),
        });
      }
      uploadFiles(filesWithBeskrivning);
    } else {
      setSelectedFiles([]);
    }
  };

  const uploadFiles = (files: IFile[]) => {
    setBlobUploadLoading(true);
    const promises = files.map(file =>
      uploadFile(file.file, activeDatabaseContext.activeDatabase ?? '')
    );

    Promise.all(promises)
      .then(responses => {
        const newSelectedFiles = responses.map((response, index) => {
          return { ...files[index], blobId: response };
        });
        setSelectedFiles(prevSelectedFiles => [
          ...prevSelectedFiles,
          ...newSelectedFiles,
        ]);
        setBlobUploadLoading(false);
      })
      .catch(error => {
        setErrorMessage(error.message);
        setBlobUploadLoading(false);
      });
  };

  const removeSelectedFile = (id: string) => {
    setSelectedFiles(prevSelectedFiles => {
      const index = prevSelectedFiles.findIndex(prevFile => prevFile.id === id);

      if (index > -1) {
        return remove(index, 1, prevSelectedFiles);
      } else {
        return prevSelectedFiles;
      }
    });
  };

  const canCommitFile =
    !blobUploadLoading &&
    selectedFiles &&
    selectedFiles.length > 0 &&
    selectedFiles.every(file => file.blobId);

  const onCommitFileAdd = () => {
    if (canCommitFile) {
      const newItems = selectedFiles.map(selectedFile => {
        const extension = getFileExtension(selectedFile.file.name);
        return {
          id: selectedFile.blobId,
          BESKRIVNING: selectedFile.beskrivning,
          BINARDATA: selectedFile.blobId,
          EXTENSION: extension,
          BINARDATASIZE: selectedFile.file.size ?? 0,
        };
      });
      props.onAddNewItem?.(newItems);
      props.onValuesCommited?.(true);
    }
  };

  const apiRef = useGridApiRef();

  return (
    <EDDialog
      title={props.title}
      open={props.open}
      onClose={props.onClose}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: () => {
            props.onClose();
          },
        },
        {
          label: 'Lägg till',
          onClick: () => {
            onCommitFileAdd();
          },
          buttonProps: {
            disabled: !canCommitFile,
            flat: false,
          },
        },
      ]}
      onExited={onDialogExited}
      maxWidth="md"
    >
      <div className={classes.contentContainer}>
        {isTouchDevice && <SelectFileButton setFiles={setFiles} />}
        {errorMessage && (
          <EDTypography color="error">{errorMessage}</EDTypography>
        )}
        {!isTouchDevice && (
          <DndProvider backend={HTML5Backend}>
            <FileDropTarget
              setFiles={files => {
                setFiles(files);
              }}
            />
          </DndProvider>
        )}
        {selectedFiles.length > 0 && (
          <EDDataGrid
            columns={[
              {
                field: 'beskrivning',
                headerName: 'Beskrivning',
                editable: true,
                renderEditCell: params =>
                  renderBeskrivningCell(params, apiRef, onBeskrivningChange),
                renderCell: params =>
                  renderBeskrivningCell(params, apiRef, onBeskrivningChange),
                flex: 1,
              },
              {
                field: 'filename',
                headerName: 'Filnamn',
                valueGetter: (_, row) => row.file.name,
                flex: 1,
              },
              {
                field: 'delete',
                headerName: 'Ta bort',
                renderCell: params => {
                  return (
                    <EDIconButton
                      tooltip="Ta bort"
                      icon="delete"
                      onClick={() => {
                        removeSelectedFile(params.id as string);
                      }}
                      margin="none"
                    />
                  );
                },
                renderHeader: () => null,
                hideSortIcons: true,
                disableColumnMenu: true,
                disableReorder: true,
                width: 70,
              },
            ]}
            rows={selectedFiles}
            autoHeight
            apiRef={apiRef}
          />
        )}
      </div>
    </EDDialog>
  );
};

export default FakturaDokumentAddComp;
